package com.banyan.flightviewer.web.components.airport

import com.banyan.flightviewer.data.AirlineDatabase
import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AirportInfo
import com.banyan.flightviewer.util.GeoUtils
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.dom.span
import com.banyan.flightviewer.web.state.WebAppContainer
import org.w3c.dom.Element

/**
 * "On ground" tab: lists live aircraft currently on the tarmac within
 * ~3km of the airport centerpoint.
 *
 * On-ground criterion mirrors desktop:
 *   altitude == "ground" OR (altitudeNumeric <= 0 AND speed < 30 kts)
 *
 * Subscribes to `appState.aircraftList` (NOT the filtered list -- filters
 * apply to the main table, but hiding on-ground aircraft inside an
 * airport's dedicated tab would be confusing). Re-renders whenever the
 * live list changes so moving/landing aircraft appear and disappear as
 * they cross the radius.
 */
fun mountAirportOnGroundTab(parent: Element, app: WebAppContainer, code: String) {
    val container = parent.div(className = "fv-airport__onground")

    // Resolve the airport centerpoint. Prefer the fresh AirportInfo from the
    // FR24 cache; fall back to the bundled database so the tab still works
    // when FR24 is unavailable or the airport is missing from FR24.
    val fallback = AirportDatabase.getAirportsWithLocations()
        .firstOrNull { it.code.equals(code, ignoreCase = true) }
    val centerLat: Double
    val centerLon: Double
    val cachedInfo: AirportInfo? = AirportCache.getInfo(code)
    if (cachedInfo != null && (cachedInfo.lat != 0.0 || cachedInfo.lon != 0.0)) {
        centerLat = cachedInfo.lat
        centerLon = cachedInfo.lon
    } else if (fallback != null) {
        centerLat = fallback.lat
        centerLon = fallback.lon
    } else {
        container.div(className = "fv-airport__empty") {
            textContent = "Airport location unavailable"
        }
        return
    }

    fun filter(list: List<AircraftData>): List<AircraftData> = list.filter { ac ->
        if (!isOnGround(ac)) return@filter false
        val distKm = GeoUtils.haversineDistance(centerLat, centerLon, ac.lat, ac.lon)
        distKm < 3.0
    }

    fun render(list: List<AircraftData>) {
        container.removeAllChildren()
        val on = filter(list).sortedBy { it.callsign }
        if (on.isEmpty()) {
            container.div(className = "fv-airport__empty") {
                textContent = "No aircraft on ground"
            }
            return
        }
        on.forEach { ac -> renderGroundRow(container, ac) }
    }

    app.appState.scope.bindFlow(app.appState.aircraftList) { render(it) }
}

private fun isOnGround(ac: AircraftData): Boolean {
    if (ac.altitude.equals("ground", ignoreCase = true)) return true
    val alt = ac.altitude.toDoubleOrNull() ?: return false
    return alt <= 0 && ac.speed < 30
}

private fun renderGroundRow(parent: Element, ac: AircraftData) {
    val airlineName = AirlineDatabase.getAirlineName(ac.callsign)

    parent.div(className = "fv-airport__flight-row") {
        // Column 1: callsign + airline
        div(className = "fv-airport__ground-callsign") {
            div(className = "fv-airport__flight-time-big") { textContent = ac.callsign }
            if (airlineName != null) {
                div(className = "fv-airport__flight-status") { textContent = airlineName }
            }
        }

        // Column 2: registration + type badge + route
        div(className = "fv-airport__flight-dest") {
            div(className = "fv-airport__flight-dest-line") {
                if (ac.registration.isNotBlank()) {
                    span(className = "fv-airport__flight-destname") {
                        textContent = ac.registration.uppercase()
                    }
                }
                if (ac.aircraftType.isNotBlank() && ac.aircraftType != "Unknown") {
                    span(className = "fv-airport__type-badge") {
                        textContent = ac.aircraftType
                    }
                }
            }
            val route = listOf(ac.origin, ac.destination).filter { it.isNotBlank() && it != "_none" }
            if (route.isNotEmpty()) {
                div(className = "fv-airport__flight-dest-line") {
                    span(className = "fv-airport__flight-num") {
                        textContent = route.joinToString(" \u2192 ")
                    }
                }
            }
        }
    }
}
